import { useState } from "react";
import { portalApi, portalError, euros, frDate } from "@/lib/weddingApi";
import { Info, Send } from "lucide-react";

const SPACES = {
  petit_salon: "Petit Salon",
  grand_salon: "Grand Salon",
  etablissement: "Établissement entier",
};

function Row({ label, value }) {
  if (value === "" || value == null) return null;
  return (
    <div className="flex items-baseline justify-between gap-6 py-2.5 border-b border-dashed border-[#D9D3C5]">
      <span className="text-sm text-[#697585]">{label}</span>
      <span className="text-sm text-[#203240] text-right font-medium">{value}</span>
    </div>
  );
}

export default function ReceptionTab({ wedding, reload, setError }) {
  const c = wedding.contract || {};
  const b = wedding.budget || {};
  const [kind, setKind] = useState("guests");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (message.trim().length < 3) return;
    try {
      await portalApi.post("/change-requests", { kind, message: message.trim() });
      setMessage("");
      setSent(true);
      reload();
    } catch (e) {
      setError(portalError(e));
    }
  };

  return (
    <div data-testid="reception-tab" className="space-y-10">
      <section className="bg-white border border-[#D9D3C5] p-6 md:p-8">
        <h2 className="font-display text-2xl font-light">Votre réception</h2>
        <p className="mt-2 text-sm text-[#697585]">{wedding.event_type_label} · {wedding.event_title}</p>
        <div className="mt-5">
          <Row label="Date" value={frDate(wedding.event_date)} />
          <Row label="Arrivée des invités" value={wedding.arrival_time} />
          <Row label="Début" value={wedding.start_time} />
          <Row label="Fin prévue" value={wedding.end_time} />
          <Row label="Fin maximale" value={c.end_time_max} />
          <Row label="Espace" value={c.space ? `${SPACES[c.space] || c.space}${c.space_confirmed ? "" : " (à confirmer)"}` : ""} />
          <Row label="Durée incluse" value={c.included_minutes ? `${Math.round(c.included_minutes / 60)} h` : ""} />
          <Row label="À finaliser avant le" value={wedding.deadline_date ? frDate(wedding.deadline_date) : "date à confirmer avec le restaurant"} />
        </div>
        {wedding.late && (
          <p data-testid="reception-late" className="mt-5 text-sm text-[#B8956A] border-l-2 border-[#B8956A] pl-3">
            La date limite est dépassée : merci de finaliser vos informations, nous restons à votre disposition.
          </p>
        )}
      </section>

      <section className="bg-white border border-[#D9D3C5] p-6 md:p-8">
        <h2 className="font-display text-2xl font-light">Le budget convenu</h2>
        <div className="mt-5">
          <Row label={`Adultes (${b.billable_adults || 0} × ${euros(c.adult_price_cents)})`} value={euros(b.adult_total_cents)} />
          <Row label={`Enfants jusqu'à ${c.child_max_age || 12} ans (${b.billable_children || 0} × ${euros(c.child_price_cents)})`} value={euros(b.child_total_cents)} />
          {!!b.cocktail_participants && (
            <Row label={`Cocktail (${b.cocktail_participants} × ${euros(c.cocktail_price_cents)})`} value={euros(b.cocktail_total_cents)} />
          )}
          {!!b.cheese_participants && (
            <Row label={`Assiette de fromages (${b.cheese_participants} × ${euros(c.cheese_price_cents)})`} value={euros(b.cheese_total_cents)} />
          )}
          {!!b.extra_half_hours && (
            <Row label={`Dépassement (${b.extra_half_hours} × 30 min)`} value={euros(b.extra_total_cents)} />
          )}
          <Row label="Privatisation" value={euros(b.privatisation_cents)} />
          <div className="flex items-baseline justify-between gap-6 pt-4">
            <span className="overline">Total convenu</span>
            <span data-testid="reception-total" className="font-display text-2xl text-[#B8956A]">{euros(b.total_cents)}</span>
          </div>
          <Row label={`Acompte (${b.deposit_percent || 0} %)`} value={euros(b.deposit_cents)} />
          <Row label="Solde" value={`${euros(b.balance_cents)}${c.balance_terms ? ` — ${c.balance_terms}` : ""}`} />
        </div>
        <p className="mt-5 flex gap-2 text-sm text-[#697585]">
          <Info size={16} className="shrink-0 mt-0.5 text-[#B8956A]" />
          {wedding.privatisation_note}
        </p>
      </section>

      {(c.inclusions?.length || c.exclusions?.length || c.conditions_text) && (
        <section className="bg-white border border-[#D9D3C5] p-6 md:p-8">
          <h2 className="font-display text-2xl font-light">Vos conditions</h2>
          {!!c.inclusions?.length && (
            <ul className="mt-5 space-y-2">
              {c.inclusions.map((i, idx) => (
                <li key={idx} className="text-sm text-[#203240] flex gap-2"><span className="text-[#B8956A]">·</span>{i}</li>
              ))}
            </ul>
          )}
          {!!c.exclusions?.length && (
            <ul className="mt-5 space-y-2">
              {c.exclusions.map((i, idx) => (
                <li key={idx} className="text-sm text-[#697585] flex gap-2"><span className="text-[#697585]">·</span>{i}</li>
              ))}
            </ul>
          )}
          {c.conditions_text && <p className="mt-5 text-sm text-[#697585] whitespace-pre-line">{c.conditions_text}</p>}
        </section>
      )}

      <section className="bg-white border border-[#D9D3C5] p-6 md:p-8">
        <h2 className="font-display text-2xl font-light">Demander une modification</h2>
        <p className="mt-2 text-sm text-[#697585]">
          Nombre d'invités, option ou horaire : votre demande est transmise au restaurant pour accord.
        </p>
        <div className="mt-5 grid gap-3">
          <select data-testid="change-request-kind" className="field" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="guests">Nombre d'invités</option>
            <option value="option">Option (cocktail, fromage…)</option>
            <option value="schedule">Horaires</option>
            <option value="other">Autre</option>
          </select>
          <textarea
            data-testid="change-request-message"
            className="field min-h-[110px]"
            placeholder="Expliquez votre demande…"
            value={message}
            onChange={(e) => { setMessage(e.target.value); setSent(false); }}
          />
          <button data-testid="change-request-submit" className="btn-primary justify-self-start inline-flex items-center gap-2" onClick={submit}>
            <Send size={14} /> Envoyer la demande
          </button>
          {sent && <p data-testid="change-request-sent" className="text-sm text-emerald-700">Demande transmise au restaurant.</p>}
        </div>

        {!!wedding.change_requests?.length && (
          <ul className="mt-7 space-y-3">
            {wedding.change_requests.map((cr) => (
              <li key={cr.id} data-testid={`change-request-${cr.id}`} className="border border-[#D9D3C5] p-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="overline">{cr.kind}</span>
                  <span className={`text-xs uppercase tracking-widest ${cr.state === "approved" ? "text-emerald-700" : cr.state === "refused" ? "text-red-600" : "text-[#B8956A]"}`}>
                    {cr.state === "approved" ? "Acceptée" : cr.state === "refused" ? "Refusée" : "En attente"}
                  </span>
                </div>
                <p className="mt-2 text-sm text-[#203240]">{cr.message}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
